import { create } from "zustand";
import { User } from "../types/user";
import api from "../service/api";
import { useUIStore } from "./uiStore";

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isInitializing: boolean; // Uygulama yetki kontrolü yapıyor mu?

  login: (user: User, accessToken: string, refreshToken: string) => void;
  logout: () => void;
  setToken: (accessToken: string, refreshToken: string) => void;
  setUser: (user: User) => void;
  checkAuth: () => Promise<void>; // Backend ile yetki tazeleyen metod
}

const safeJsonParse = (key: string) => {
  const item = localStorage.getItem("user");
  if (!item || item === "undefined") return null;
  try {
    return JSON.parse(item);
  } catch (e) {
    return null;
  }
};

export const useAuthStore = create<AuthState>((set, get) => ({
  // İlk yükleme LocalStorage'dan
  user: safeJsonParse("user"),
  accessToken: localStorage.getItem("accessToken"),
  refreshToken: localStorage.getItem("refreshToken"),
  isAuthenticated: !!localStorage.getItem("accessToken"),
  isInitializing: true, // Başlangıçta true yaparak App.tsx'te splash göstermeyi sağlarız

  login: (user, accessToken, refreshToken) => {
    localStorage.setItem("user", JSON.stringify(user));
    localStorage.setItem("accessToken", accessToken);
    localStorage.setItem("refreshToken", refreshToken);
    set({
      user,
      accessToken,
      refreshToken,
      isAuthenticated: true,
      isInitializing: false
    });

    if (user.settings?.uiSettings) {
      useUIStore.getState().hydrateSettings(user.settings.uiSettings);
    }
  },

  logout: () => {
    localStorage.removeItem("user");
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    set({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isInitializing: false
    });
  },

  setToken: (accessToken, refreshToken) => {
    localStorage.setItem("accessToken", accessToken);
    localStorage.setItem("refreshToken", refreshToken);
    set({ accessToken, refreshToken });
  },

  setUser: (user) => {
    localStorage.setItem("user", JSON.stringify(user));
    set({ user, isAuthenticated: true });
  },

  /**
   * checkAuth: Sayfa yenilendiğinde veya uygulama açıldığında
   * token üzerinden en güncel user & permission verisini çeker.
   */
  checkAuth: async () => {
    const { accessToken, logout, setUser } = get();

    // Eğer token yoksa direkt yükleme bitti de
    if (!accessToken) {
      set({ isInitializing: false });
      return;
    }

    try {
      // Backend'deki @Get("me") endpoint'ine istek atar
      // Bu endpoint prisma.user.findUnique({ include: { role: { include: { permissions: true } } } }) dönmelidir.
      const response = await api.get("/auth/me");

      if (response.data) {
        setUser(response.data);
        if (response.data.settings?.uiSettings) {
          useUIStore.getState().hydrateSettings(response.data.settings.uiSettings);
        }
      }
    } catch (error) {
      console.error("Auth senkronizasyon hatası:", error);
      // Token geçersizse veya kullanıcı silindiyse logout yap
      logout();
    } finally {
      // İşlem bitince splash'i kapatmak için isInitializing'i false yap
      set({ isInitializing: false });
    }
  },
}));